import i18next from 'i18next'
import * as React from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import {
  NestedValidatorResult,
  SingleValidatorResult,
} from '../../validator/validatorExpr'
import { getClassName } from './utils'
import { Link } from 'widgets-for-react'

type FormStyle = Partial<{
  blockClassName: string
  headerClassName: string
  subHeaderClassName: string
  innerClassName: string
  titleClassName: string
  subTitleClassName: string
  introClassName: string
  formClassName: string
  formInnerClassName: string
  formAlertClassName: string
  formAlertErrorClassName: string
  formAlertWarningClassName: string
  formAlertSuccessClassName: string
  formGroupClassName: string
  formGroupCheckboxClassName: string
  formGroupRadioClassName: string
  formBooleanLabelClassName: string
  formBooleanInputClassName: string
  formBooleanErrorClassName: string
  formInputClassName: string
  formSelectClassName: string
  formLabelClassName: string
  formFieldErrorClassName: string
  formFieldFeedbackInvalidClassName: string
  formFieldFeedbackClassName: string
  formActionsClassName: string
  clickableWrapperClassName: string
  clickablePrimaryClassName: string
  clickableSecondaryClassName: string
  clickableTertiaryClassName: string
  footerClassName: string
  footerItemClassName: string
}>

export const formClasses = {
  blockClassName: 'loginflow lf',
  headerClassName: 'lf__header',
  subHeaderClassName: 'lf__subheader',
  titleClassName: 'lf__title title--s',
  subTitleClassName: 'lf__subtitle title--xs',
  introClassName: 'lf__intro text--styled',
  formClassName: 'lf__form',
  formInnerClassName: 'lf__form-inner',
  formAlertClassName: 'lf__alert text--styled',
  formAlertErrorClassName: 'lf__alert--error',
  formAlertWarningClassName: 'lf__alert--warning',
  formAlertSuccessClassName: 'lf__alert--success',
  formGroupClassName: 'lf__group',
  formGroupCheckboxClassName:
    'lf__group lf__group--checkbox form-group custom-control custom-checkbox',
  formGroupRadioClassName:
    'lf__group lf__group--radio form-group custom-control custom-radio',
  formLabelClassName: 'lf__label form-label typo--xs weight--bold',
  formInputClassName: 'lf__input',
  formSelectClassName: 'lf__select custom-select',
  formBooleanInputClassName: 'custom-control-input',
  formBooleanLabelClassName: 'custom-control-label text--styled',
  formFieldErrorClassName: 'lf__invalid is-invalid',
  formFieldFeedbackInvalidClassName: 'lf__error invalid-feedback',
  formFieldFeedbackClassName: 'lf__description',
  formActionsClassName: 'lf__actions',
  clickableWrapperClassName: 'lf__action',
  clickablePrimaryClassName: '',
  clickableSecondaryClassName: '',
  clickableTertiaryClassName: '',
  footerClassName: 'lf__options text--styled small',
  footerItemClassName: 'lf__option',
}

// ==========================================================================
// FORM CONTROLS & BUTTONS
// ==========================================================================

type FormItemBaseProps = {
  label: string | JSX.Element
  controlId: string
  readOnly?: boolean
  required: boolean
  fieldMessage?: string
}

type FormControlProps = {
  type?: string
  rows?: number
  as?: React.ElementType
  value?: string
  onChange: (_: string) => void
  onBlur?: () => void
  validation: SingleValidatorResult | NestedValidatorResult | undefined
  children?: JSX.Element | Array<JSX.Element>
  placeholder?: string
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search'
  showPasswordToggle?: {
    isChecked: boolean
    label: string
    title?: string
    onChange: (_: boolean) => void
  }
  maxLength?: number
} & FormItemBaseProps

type FormSelectProps = {
  value?: string
  onChange: (_: string) => void
  onBlur?: () => void
  validation: SingleValidatorResult | NestedValidatorResult | undefined
  children?: JSX.Element | Array<JSX.Element>
} & FormItemBaseProps

type FormBooleanProps = {
  checked: boolean
  onChange: (_: boolean) => void
  onBlur?: () => void
  validation: SingleValidatorResult | undefined
} & FormItemBaseProps

type FormRadioFieldsetItemProps = {
  label: string | JSX.Element
  controlId: string
  checked: boolean
  onChange: (_: boolean) => void
}

type FormRadioFieldsetProps = {
  legend: string
  name: string
  required: boolean
  readOnly?: boolean
  fieldMessage?: string
  items: FormRadioFieldsetItemProps[]
  validation: SingleValidatorResult | undefined
}

type ButtonProps = {
  label: string | JSX.Element
  disabled: boolean
} & (
  | {
      kind: 'button'
      onClick: () => void
      type?: 'button' | 'submit'
    }
  | {
      kind: 'link'
      to: string
      target?: string
      onClick?: () => void
    }
)

const fieldErrorClassName = getClassName(
  formClasses,
  'formFieldErrorClassName',
  ''
)

export const FormControls = {
  SubmittingSpinner: (props: {}) => (
    <Spinner animation="border" role="status" as="span" size="sm">
      <span className="sr-text">{i18next.t('global:Submitting')}</span>
    </Spinner>
  ),
  SubmittingSpinnerOrLabel: (props: { showLoader: boolean; label: string }) =>
    props.showLoader ? <FormControls.SubmittingSpinner /> : <>{props.label}</>,
  OptionalLabel: ({ required }: { required: boolean }) =>
    !required ? <span>{` (${i18next.t('common:optional')})`}</span> : <></>,
  FormItemInGroup: (props: FormControlProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.controlId}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.controlId}-form-message`
    const inputClassName = getClassName(formClasses, 'formInputClassName', '')
    const selectClassName = getClassName(formClasses, 'formSelectClassName', '')

    return (
      <Form.Group
        controlId={props.controlId}
        className={getClassName(formClasses, 'formGroupClassName', '')}
      >
        <Form.Label
          className={getClassName(formClasses, 'formLabelClassName', '')}
        >
          {props.label}
          <FormControls.OptionalLabel {...props} />
        </Form.Label>

        <Form.Control
          type={props.type}
          as={props.as}
          rows={props.rows}
          value={props.value || ''}
          placeholder={props.placeholder}
          inputMode={props.inputMode || 'text'}
          readOnly={props.readOnly}
          disabled={props.readOnly}
          aria-required={props.required}
          isInvalid={isInvalid ? true : undefined}
          aria-invalid={isInvalid}
          aria-describedby={
            isInvalid ? errorMessageId : hasFieldMessage ? fieldMessageId : ''
          }
          onChange={(e) => props.onChange(e.currentTarget.value)}
          onBlur={() => {
            if (props.onBlur) props.onBlur()
          }}
          className={
            (props.as === 'select' ? selectClassName : inputClassName) +
            (isInvalid ? ` ` : '')
          }
          maxLength={props.maxLength}
        >
          {props.children}
        </Form.Control>

        {/* Validation feedback */}
        {hasErrorMessage && (
          <Form.Control.Feedback
            as="p"
            role="alert"
            type="invalid"
            id={errorMessageId}
            className={getClassName(
              formClasses,
              'formFieldFeedbackInvalidClassName',
              ''
            )}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getClassName(
              formClasses,
              'formFieldFeedbackClassName',
              ''
            )}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </Form.Group>
    )
  },
  FormSelectInGroup: (props: FormSelectProps): JSX.Element => {
    return (
      <FormControls.FormItemInGroup as="select" {...props}>
        {props.children}
      </FormControls.FormItemInGroup>
    )
  },
  FormCheckInGroup: (props: FormBooleanProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.controlId}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.controlId}-form-message`
    const checkboxClassName = getClassName(
      formClasses,
      'formBooleanInputClassName',
      ''
    )
    const checkboxLabelClassName = getClassName(
      formClasses,
      'formBooleanLabelClassName',
      ''
    )
    return (
      <Form.Group
        controlId={props.controlId}
        className={getClassName(
          formClasses,
          'formGroupClassName',
          'form-group'
        )}
      >
        <div
          className={`custom-control custom-checkbox ${
            isInvalid ? fieldErrorClassName : ''
          }`}
        >
          <Form.Check.Input
            type="checkbox"
            readOnly={props.readOnly}
            disabled={props.readOnly}
            checked={props.checked}
            isInvalid={isInvalid ? true : undefined}
            aria-invalid={isInvalid}
            aria-describedby={
              isInvalid ? errorMessageId : hasFieldMessage ? fieldMessageId : ''
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              props.onChange(e.currentTarget.checked)
            }
            className={checkboxClassName + (isInvalid ? ` ` : '')}
          />

          {typeof props.label === 'string' ? (
            <label
              className={checkboxLabelClassName}
              htmlFor={props.controlId}
              dangerouslySetInnerHTML={{ __html: props.label }}
            />
          ) : (
            <label className={checkboxLabelClassName} htmlFor={props.controlId}>
              {props.label}
              <FormControls.OptionalLabel {...props} />
            </label>
          )}
        </div>

        {hasErrorMessage && (
          <Form.Control.Feedback
            type="invalid"
            id={errorMessageId}
            className={getClassName(
              formClasses,
              'formFieldFeedbackInvalidClassName',
              ''
            )}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getClassName(
              formClasses,
              'formFieldFeedbackClassName',
              ''
            )}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </Form.Group>
    )
  },
  FormRadioInGroup: (props: FormRadioFieldsetProps): JSX.Element => {
    const isInvalid = props.validation && props.validation.kind === 'failed'
    const hasErrorMessage =
      props.validation &&
      props.validation.kind === 'failed' &&
      props.validation.message
    const errorMessageId = `a11y-${props.name}-form-error`
    const hasFieldMessage = props.fieldMessage
    const fieldMessageId = `a11y-${props.name}-form-message`
    const radioClassName = getClassName(
      formClasses,
      'formBooleanInputClassName',
      ''
    )
    const radioLabelClassName = getClassName(
      formClasses,
      'formBooleanLabelClassName',
      ''
    )
    return (
      <fieldset
        className={`${getClassName(
          formClasses,
          'formGroupClassName',
          'form-group'
        )} form-group`}
      >
        <legend className={getClassName(formClasses, 'formLabelClassName', '')}>
          {props.legend}
          <FormControls.OptionalLabel {...props} />
        </legend>
        {props.items.length > 0 &&
          props.items.map((item) => (
            <div
              className={`custom-control custom-radio ${
                isInvalid ? fieldErrorClassName : ''
              }`}
              key={item.controlId}
            >
              <Form.Check.Input
                type="radio"
                name={props.name}
                id={item.controlId}
                readOnly={props.readOnly}
                disabled={props.readOnly}
                checked={item.checked}
                isInvalid={isInvalid ? true : undefined}
                aria-invalid={isInvalid}
                aria-describedby={
                  isInvalid
                    ? errorMessageId
                    : hasFieldMessage
                    ? fieldMessageId
                    : ''
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  item.onChange(e.currentTarget.checked)
                }
                className={radioClassName + (isInvalid ? ` ` : '')}
              />
              <label className={radioLabelClassName} htmlFor={item.controlId}>
                {item.label}
              </label>
            </div>
          ))}

        {hasErrorMessage && (
          <Form.Control.Feedback
            as="p"
            role="alert"
            type="invalid"
            id={errorMessageId}
            className={getClassName(
              formClasses,
              'formFieldFeedbackInvalidClassName',
              ''
            )}
          >
            {hasErrorMessage}
          </Form.Control.Feedback>
        )}

        {hasFieldMessage && (
          <Form.Text
            as="p"
            id={fieldMessageId}
            className={getClassName(
              formClasses,
              'formFieldFeedbackClassName',
              ''
            )}
          >
            {hasFieldMessage}
          </Form.Text>
        )}
      </fieldset>
    )
  },
  ButtonPrimary: (p: ButtonProps) => {
    return p.kind == 'button' ? (
      <Button
        type={p.type || 'button'}
        variant="dark"
        className={getClassName(formClasses, 'clickablePrimaryClassName', '')}
        disabled={p.disabled}
        onClick={p.onClick}
      >
        <span className="btn__label">{p.label}</span>
      </Button>
    ) : (
      <Link
        to={p.to}
        target={p.target}
        className={`${getClassName(
          formClasses,
          'clickablePrimaryClassName',
          'btn btn-dark'
        )}${p.disabled ? ' disabled' : ''}`}
        onClick={p.onClick || undefined}
      >
        <span className="btn__label">{p.label}</span>
      </Link>
    )
  },
  ButtonSecondary: (p: ButtonProps) => {
    return p.kind == 'button' ? (
      <Button
        type={p.type || 'button'}
        variant="light"
        className={getClassName(
          formClasses,
          'clickableSecondaryClassName',
          'btn btn-light'
        )}
        disabled={p.disabled}
        onClick={p.onClick}
      >
        <span className="btn__label">{p.label}</span>
      </Button>
    ) : (
      <Link
        to={p.to}
        target={p.target}
        className={`${getClassName(
          formClasses,
          'clickableSecondaryClassName',
          'btn btn-light'
        )}${p.disabled ? ' disabled' : ''}`}
        onClick={p.onClick || undefined}
      >
        <span className="btn__label">{p.label}</span>
      </Link>
    )
  },
}

// ==========================================================================
// NOTIFICATIONS
// ==========================================================================

type NotificationStatusTypes = 'error' | 'warning' | 'success'

export const FormNotification = (p: {
  message: string | JSX.Element
  status?: NotificationStatusTypes
  extraClasses?: string
}) => {
  const alertClassName = getClassName(
    formClasses,
    'formAlertClassName',
    'alert small'
  )
  const alertErrorClassName = getClassName(
    formClasses,
    'formAlertErrorClassName',
    'alert-danger'
  )
  const alertWarningClassName = getClassName(
    formClasses,
    'formAlertWarningClassName',
    'alert-warning'
  )
  const alertSuccessClassName = getClassName(
    formClasses,
    'formAlertSuccessClassName',
    'alert-success'
  )
  const alertKindCheck =
    p.status == 'success'
      ? alertSuccessClassName
      : p.status == 'warning'
      ? alertWarningClassName
      : alertErrorClassName
  return (
    <div
      role="alert"
      className={`${alertClassName} ${
        p.status ? alertKindCheck : alertErrorClassName
      } ${p.extraClasses || ''}`}
    >
      {typeof p.message == 'string' ? (
        <div dangerouslySetInnerHTML={{ __html: p.message }} />
      ) : (
        <div>{p.message}</div>
      )}
    </div>
  )
}
